/**
 * @copyright Copyright 2021-2023 Epic Systems Corporation
 * @file Live apps dashboard widget
 * @module Epic.AppOrchard.Dashboard.Widgets.LiveAppsWidget
 */

import { ISuccessHandler, useAsync } from "@epic/react-async-hook";
import { useDispatch } from "@epic/react-redux-booster";
import { config } from "ao/appConfig";
import { AsyncPlaceholder, ITab, SearchInput, TabbedSection } from "ao/components/Core";
import { getLiveApps } from "ao/components/Dashboard/data";
import { useSiteInfoState } from "ao/state/siteInfo";
import { useLiveAppsState } from "ao/state/widgets/liveApps";
import { updateAppsAfterLoad } from "ao/store-methods/liveApps";
import { IWidget, IWidgetProps } from "ao/types/dashboard";
import { getBaseUrl } from "ao/utils/helpers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { WidgetSpinner, WidgetTable } from "../../widgetHelpers";
import { LiveAppRow } from "./helperComponents/LiveAppRow";

/** Live apps dashboard widget */
export const LiveAppsWidget: IWidget = (props: IWidgetProps) => {
	//#region state and props

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const { isExpanded, orgId, onSubtitleUpdate, userSecurity } = props;
	const [searchTerm, setSearchTerm] = useState<string>("");
	const handleChangeSearchTerm = useCallback((newSearchTerm: string) => setSearchTerm(newSearchTerm), []);
	const baseUrl = useMemo(() => getBaseUrl(), []);
	const entriesPerPage = config.Dashboard.WidgetAppRowsPerPage;
	const monthsAgoForLiveAppsHeader = config.Dashboard.MonthsAgoForLiveAppsHeader;

	const dispatch = useDispatch();

	const hasLoaded = useLiveAppsState((selectors) => selectors.getHasLoaded(orgId), [orgId]);

	const allApps = useLiveAppsState((selectors) => selectors.getAllApps({ orgId, searchTerm }), [
		orgId,
		searchTerm,
	]);

	const numApsLiveInLastXMonths = useLiveAppsState(
		(selectors) =>
			selectors.getNumAppsLiveInLastXMonths({ orgId, monthsAgo: monthsAgoForLiveAppsHeader }),
		[orgId],
	);

	const handleAppsLoadedSuccess = useCallback<ISuccessHandler<typeof getLiveApps>>(
		(response, extras) => {
			const [orgId] = extras.params;
			updateAppsAfterLoad(dispatch, orgId, response.liveApps);
		},
		[dispatch],
	);

	const [LoadingPlaceholder, loadApps] = useAsync(getLiveApps, AsyncPlaceholder, {
		executeImmediately: false,
		displayName: "getLiveApps",
		// `extras` will contain `params` field which has the params passed to getInProgressApps (namely the org ID)
		// this is needed rather than using the orgId from props directly in handleAppsLoaded because
		// that will result in a stale closure (https://gitlab.epic.com/foundations/react-async-hook/-/issues/2)
		onSuccess: (response, extras) => handleAppsLoadedSuccess(response, extras),
	});

	useEffect(() => {
		// if/when this issue (https://gitlab.epic.com/foundations/react-async-hook/-/issues/3) is fixed
		// we can use !hasLoaded as a executeImmediately param for useAsync instead to be a bit cleaner
		if (!hasLoaded) {
			loadApps(orgId);
		}
		if (numApsLiveInLastXMonths !== undefined) {
			const subtitle = `${numApsLiveInLastXMonths} live in the last ${monthsAgoForLiveAppsHeader} months`;
			onSubtitleUpdate(subtitle);
		}
	}, [hasLoaded, loadApps, orgId, numApsLiveInLastXMonths, onSubtitleUpdate, monthsAgoForLiveAppsHeader]);

	// //#endregion

	const tabs: ITab[] = [
		{
			tabName: "Live Apps",
			countPrefix: allApps.length,
			helptext: `All live ${config.SiteName} apps`,
			content: (
				<WidgetTable
					noRecordsMessage="No matching apps"
					entriesPerPage={!isExpanded ? entriesPerPage : undefined}
					isExpanded={isExpanded}
				>
					{allApps.map((app) => (
						<LiveAppRow
							app={app}
							key={app.appId}
							orgId={orgId}
							baseUrl={baseUrl}
							userSecurity={userSecurity}
							isGalleryEnabled={siteInfo.isGalleryEnabled}
						></LiveAppRow>
					))}
				</WidgetTable>
			),
		},
	];

	return (
		<>
			{isExpanded && ( //show search bar if in expanded/popup mode
				<SearchInput
					placeholder="Search for apps by app name, company name, client ID"
					onChangeCallback={handleChangeSearchTerm}
					inputGroupProps={{ mb: "1rem" }}
				></SearchInput>
			)}
			<LoadingPlaceholder
				inProgressIndicator={<WidgetSpinner />}
				errorMessage="Error loading apps from the server"
				useLastRequestForErrors
			>
				<TabbedSection tabs={tabs} />
			</LoadingPlaceholder>
		</>
	);
};

LiveAppsWidget.descriptor = "liveApps";
